import IdentityHeader from "@/components/common/IdentityHeader";
import { NavigationTabs } from "@/components/common/NavigationTabs";
import DataTableActionMenu from "@/components/datatable/components/TableActionMenu";
import ProfileSkeleton from "@/components/preview/ProfileSkeleton";
import StatusBadge from "@/components/common/StatusBadge";
import StatStrip from "@/components/common/StatStrip";
import useConfirm from "@/hooks/useConfirm";
import useFormatter from "@/hooks/useFormatter";
import { getEmployeeAvatarColor, getInitials } from "@/lib/utils";
import { employeesKeys, kpisKeys } from "@/query-keys";
import { services } from "@/services";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { BanknoteArrowUp, Calendar, Clock, Hammer, HandCoins, MapPin, Phone, User, UserX } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Employee } from "@/schemas/employeeSchema";
import useEmployeeQuery from "../_hooks/useEmployeeQuery";
import useEmployeeMutation from "../_hooks/useEmployeeMutation";
import useEmployeeKpis from "../_hooks/useEmployeeKpis";

export const Route = createFileRoute("/_private/employees/$employeeId")({
  staticData: {
    breadcrumb: (matched) => ({ label: matched.loaderData?.name, translate: false }),
  },
  loader: async ({ params, context: { queryClient } }) => {
    const employee = await queryClient.ensureQueryData({
      queryKey: employeesKeys.detail(params.employeeId),
      queryFn: () => services.getEmployee(params.employeeId),
    });
    queryClient.ensureQueryData({
      queryKey: kpisKeys.employees(params.employeeId),
      queryFn: () => services.getEmployeeKpis(params.employeeId),
    });
    return employee;
  },
  component: RouteComponent,
  pendingComponent: ProfileSkeleton,
});

const TABS = [
  { value: "igold-transactions", icon: Hammer, labelKey: "kpis.igoldTransactions" },
  { value: "icash-transactions", icon: BanknoteArrowUp, labelKey: "kpis.icashTransactions" },
  { value: "salaries", icon: HandCoins, labelKey: "kpis.salaries" },
] as const;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useParams();
  const loaderData = Route.useLoaderData();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { t } = useTranslation();
  const { phone, weight, currency, date } = useFormatter();
  const { data } = useEmployeeQuery(params.employeeId, loaderData);
  const { deleteEmployee, toggleActiveStatus, isDeleting, isToggling } = useEmployeeMutation();
  const { data: kpis } = useEmployeeKpis(params.employeeId);

  const userTabs = useMemo(() => TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) })), [t]);

  const getCurrentTab = () => {
    if (pathname.includes("/icash-transactions")) return "icash-transactions";
    if (pathname.includes("/salaries")) return "salaries";
    return "igold-transactions";
  };

  const handleTabChange = (value: string) => {
    switch (value) {
      case "icash-transactions":
        navigate({ to: "/employees/$employeeId/icash-transactions", params });
        break;
      case "salaries":
        navigate({ to: "/employees/$employeeId/salaries", params });
        break;
      default:
        navigate({ to: "/employees/$employeeId/igold-transactions", params });
        break;
    }
  };

  if (!data) return null;

  const employee = data;
  const isDeleted = !!employee.deletedAt;

  const deleteConfirm = useConfirm({
    title: t("employees.confirm.delete.title"),
    description: t("employees.confirm.delete.description"),
    isPending: isDeleting,
  });

  const deactivateConfirm = useConfirm({
    title: t("employees.confirm.deactivate.title"),
    description: t("employees.confirm.deactivate.description"),
    isPending: isToggling,
  });

  const actions = useMemo(
    () => [
      {
        icon: (row: Employee) =>
          row.isActive ? (
            <UserX className="h-4 w-4 shrink-0" />
          ) : (
            <User className="h-4 w-4 shrink-0" />
          ),
        label: (employee: Employee) =>
          employee.isActive ? t("common.deactivate") : t("common.activate"),
        handler: (employee: Employee) =>
          deactivateConfirm.confirm(() => void toggleActiveStatus(employee.id)),
      },
    ],
    [t, deactivateConfirm, toggleActiveStatus],
  );

  const colorClass = getEmployeeAvatarColor(employee.id || employee.name || "");

  return (
    <>
      <IdentityHeader
        initials={getInitials(employee.name)}
        avatarColorClass={!isDeleted ? colorClass : undefined}
        name={employee.name}
        badge={
          <StatusBadge
            variant={employee.isActive ? "active" : "inactive"}
            label={
              employee.isActive ? t("common.active") : t("common.inactive")
            }
          />
        }
        subLabel={
          <div className="flex items-center gap-4">
            {employee.phone && (
              <span className="inline-flex items-center gap-1" dir="ltr">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {phone(employee.phone)}
              </span>
            )}
            {employee.atelierName && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {employee.atelierName}
                {employee.atelierLocation && ` (${employee.atelierLocation})`}
              </span>
            )}
          </div>
        }
        metaLabel={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {t("employees.fields.createdAt")} {date(employee.createdAt)}
            </span>
            {employee.updatedAt && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t("employees.fields.updatedAt")} {date(employee.updatedAt)}
              </span>
            )}
            {employee.createdByName && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" />
                {t("employees.fields.createdBy")} {employee.createdByName}
              </span>
            )}
          </span>
        }
        isDeleted={isDeleted}
      >
        <DataTableActionMenu
          onDelete={() => deleteConfirm.confirm(() => void deleteEmployee(employee.id))}
          row={employee}
          actions={actions}
        />
      </IdentityHeader>

      {kpis && (
        <StatStrip
          columns={[
            {
              dotClass: "bg-muted-foreground",
              label: t("kpis.employeeIcashTotalWithdrawn"),
              value: currency(kpis.icashTotalWithdrawn),
              subValue: (
                <span>
                  {t("kpis.employeeIcashCurrentMonthWithdrawn")}: {currency(kpis.icashCurrentMonthWithdrawn)}
                  {" · "}
                  {t("kpis.employeeIcashLastWithdrawalAmount")}: {currency(kpis.icashLastWithdrawalAmount)}
                </span>
              ),
            },
            {
              dotClass: "bg-primary",
              label: t("kpis.employeeActiveGoldOutstanding"),
              value: weight(kpis.igoldActiveOutstanding),
              subValue: (
                <span>
                  {t("kpis.employeeIgoldTotalLost")}: {weight(kpis.igoldTotalLost)}
                  {" · "}
                  {t("kpis.employeeIgoldCurrentMonthLost")}: {weight(kpis.igoldCurrentMonthLost)}
                </span>
              ),
            },
          ]}
        />
      )}

      <NavigationTabs activeTab={getCurrentTab()} onTabChange={handleTabChange} tabs={userTabs}>
        <Outlet />
      </NavigationTabs>

      {deleteConfirm.confirmDialog}
      {deactivateConfirm.confirmDialog}
    </>
  );
}
